<template>
	<div>
		<!-- Loading Translations -->
		<div v-if="isLoading" class="flex items-center justify-center py-8">
			<svg class="w-6 h-6 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
			<span class="ml-2 text-gray-500 text-sm">Loading translations...</span>
		</div>

		<!-- No Languages -->
		<div v-else-if="languages.length === 0" class="py-8 text-center text-sm text-gray-500">
			No languages available.
		</div>

		<!-- Translation Editor -->
		<div v-else>
			<!-- Language Tabs -->
			<div class="flex border-b border-gray-200 mb-4 overflow-x-auto">
				<button
					v-for="lang in languages"
					:key="lang.id"
					type="button"
					class="py-2 px-4 text-sm font-medium whitespace-nowrap border-none bg-transparent cursor-pointer border-b-2 -mb-px"
					:class="selectedLanguageId === lang.id ? 'text-orange-7 border-orange-7' : 'text-gray-500 border-transparent hover:text-gray-700'"
					@click="$emit('update:selectedLanguageId', lang.id)"
				>
					{{ lang.name }}
					<span
						v-if="getLanguageStatus(itemId, lang.id) === 'complete'"
						class="ml-1 w-2 h-2 rounded-full bg-green-500 inline-block"
					></span>
					<span
						v-else-if="getLanguageStatus(itemId, lang.id) === 'partial'"
						class="ml-1 w-2 h-2 rounded-full bg-orange-500 inline-block"
					></span>
					<span
						v-else
						class="ml-1 w-2 h-2 rounded-full bg-red-500 inline-block"
					></span>
				</button>
			</div>

			<!-- Translation Fields -->
			<div v-if="selectedLanguageId !== null" class="grid grid-cols-1 gap-4" :class="gridClass">
				<div v-for="field in fields" :key="field.key">
					<label class="block text-sm font-medium text-gray-700 mb-1">{{ field.label }}</label>
					<input
						v-if="field.type === 'text'"
						type="text"
						:value="getField(itemId, selectedLanguageId, field.key)"
						class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-1 focus:ring-orange-7"
						:placeholder="field.placeholder"
						@input="onFieldInput(field.key, ($event.target as HTMLInputElement).value)"
					>
					<textarea
						v-else
						:value="getField(itemId, selectedLanguageId, field.key)"
						class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-1 focus:ring-orange-7 resize-none"
						:rows="field.rows || 3"
						:placeholder="field.placeholder"
						@input="onFieldInput(field.key, ($event.target as HTMLTextAreaElement).value)"
					></textarea>
					<p v-if="field.hint" class="text-xs text-gray-400 mt-1 mb-0">{{ field.hint }}</p>
				</div>
			</div>

			<!-- Actions -->
			<div class="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
				<div class="flex items-center gap-2">
					<span
						v-if="hasUnsavedChanges(itemId)"
						class="text-xs bg-amber-100 text-amber-700 py-1 px-2 rounded-full font-medium"
					>
						Unsaved changes
					</span>
				</div>
				<div class="flex gap-2">
					<button
						type="button"
						class="py-2 px-4 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
						@click="$emit('cancel')"
					>
						Cancel
					</button>
					<button
						type="button"
						:disabled="!hasUnsavedChanges(itemId) || isSaving"
						class="py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
						@click="$emit('save')"
					>
						{{ isSaving ? 'Saving...' : 'Save Translations' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import type {
	TranslationFieldSchema,
	TranslationLanguage,
	TranslationLanguageId,
	TranslationStatus,
} from 'composables/useInlineTranslations';

export default defineComponent({
	name: 'InlineTranslationEditor',
	props: {
		itemId: {
			type: Number,
			required: true,
		},
		fields: {
			type: Array as PropType<TranslationFieldSchema[]>,
			required: true,
		},
		languages: {
			type: Array as PropType<TranslationLanguage[]>,
			required: true,
		},
		selectedLanguageId: {
			type: [String, Number] as PropType<TranslationLanguageId | null>,
			default: null,
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		isSaving: {
			type: Boolean,
			default: false,
		},
		columns: {
			type: Number,
			default: 2,
		},
		getField: {
			type: Function as PropType<(itemId: number, langId: TranslationLanguageId, key: string) => string>,
			required: true,
		},
		getLanguageStatus: {
			type: Function as PropType<(itemId: number, langId: TranslationLanguageId) => TranslationStatus>,
			required: true,
		},
		hasUnsavedChanges: {
			type: Function as PropType<(itemId: number) => boolean>,
			required: true,
		},
	},
	emits: ['update:selectedLanguageId', 'update-field', 'save', 'cancel'],
	setup(props, { emit }) {
		const gridClass = computed(() => (props.columns === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'));

		const onFieldInput = (key: string, value: string): void => {
			emit('update-field', { langId: props.selectedLanguageId, key, value });
		};

		return {
			gridClass,
			onFieldInput,
		};
	},
});
</script>
